<template>
  <v-dialog
    :value="visible"
    max-width="560"
    persistent
    @input="val => !val && close()"
  >
    <v-card class="dialog-card">
      <!-- Title bar -->
      <div class="dialog-title-bar">
        <v-icon small class="mr-2" color="blue lighten-1"
          >mdi-content-copy</v-icon
        >
        <span class="subtitle-2">Copy description to other parcels</span>
      </div>
      <v-divider></v-divider>

      <!-- Loading -->
      <v-card-text v-if="loading" class="text-center py-10">
        <v-progress-circular
          indeterminate
          color="blue darken-1"
          size="36"
        ></v-progress-circular>
        <div class="caption text--secondary mt-3">Loading parcels…</div>
      </v-card-text>

      <!-- Applying progress -->
      <v-card-text v-else-if="applying" class="py-8">
        <div class="caption text--secondary mb-4 text-center">
          Copying description…
        </div>
        <v-progress-linear
          :value="(progress.done / progress.total) * 100"
          color="blue darken-1"
          rounded
          height="8"
          class="mb-3"
        ></v-progress-linear>
        <div class="caption text--secondary text-center">
          {{ progress.done }} / {{ progress.total }} parcels updated
        </div>
      </v-card-text>

      <!-- Done -->
      <v-card-text v-else-if="done" class="text-center py-8">
        <div class="done-icon-wrap">
          <v-icon color="green darken-1" size="40"
            >mdi-check-circle-outline</v-icon
          >
        </div>
        <div class="body-2 mt-3">
          Description copied to
          <strong>{{ progress.done }}</strong>
          parcel{{ progress.done !== 1 ? 's' : '' }}.
        </div>
      </v-card-text>

      <!-- Parcel selection -->
      <template v-else>
        <v-card-text class="pb-2 pt-4">
          <!-- No eligible parcels -->
          <div
            v-if="!eligibleGroups.length"
            class="caption text--secondary text-center py-6"
          >
            There are no other parcels in this project yet.
          </div>

          <template v-else>
            <div class="caption text--secondary mb-4">
              This will overwrite the description on every parcel selected
              below.
            </div>

            <!-- Global select-all row -->
            <div class="global-select-row mb-4">
              <v-checkbox
                :input-value="allSelected"
                :indeterminate="someSelected"
                color="blue darken-1"
                dense
                hide-details
                class="ma-0 pa-0"
                label="Select all"
                @change="toggleSelectAll"
              ></v-checkbox>
              <span class="caption text--secondary ml-3">
                {{ selectedParcelIds.length }} /
                {{ eligibleParcels.length }} selected
              </span>
            </div>

            <!-- Masterplan groups, indented to mirror the project's nested
                 masterplan hierarchy -->
            <div
              v-for="group in eligibleGroups"
              :key="group.masterplanId"
              class="masterplan-group mb-3"
              :style="{ marginLeft: group.depth * 20 + 'px' }"
            >
              <!-- Group header with per-masterplan select-all checkbox -->
              <div class="group-header-row">
                <v-checkbox
                  :input-value="isGroupFullySelected(group)"
                  :indeterminate="isGroupIndeterminate(group)"
                  color="blue darken-1"
                  dense
                  hide-details
                  class="ma-0 pa-0 mr-2"
                  @change="val => toggleGroup(group, val)"
                ></v-checkbox>
                <div class="group-header-label">
                  <span v-if="group.depth > 0" class="grey--text mr-1"
                    >&#8627;</span
                  >
                  <v-icon x-small class="mr-1 group-icon"
                    >mdi-map-outline</v-icon
                  >
                  {{ group.masterplanName }}
                  <span class="group-count">({{ group.parcels.length }})</span>
                </div>
              </div>

              <!-- Parcel checkboxes -->
              <div class="group-parcels">
                <v-checkbox
                  v-for="p in group.parcels"
                  :key="p.id"
                  v-model="selectedParcelIds"
                  :value="p.id"
                  :label="p.name"
                  color="blue darken-1"
                  dense
                  hide-details
                  class="ma-0 pa-0 parcel-checkbox"
                ></v-checkbox>
              </div>
            </div>
          </template>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text small @click="close">Cancel</v-btn>
          <v-btn
            color="blue darken-1"
            small
            :disabled="!selectedParcelIds.length"
            @click="copyDescription"
          >
            Copy &amp; Save
          </v-btn>
        </v-card-actions>
      </template>

      <!-- Done actions -->
      <template v-if="done">
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" small @click="close">Done</v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import ParcelService from '../services/ParcelService'
import MasterplanHelper from '../helpers/MasterplanHelper'

export default {
  name: 'CopyParcelDescriptionDialog',
  props: {
    visible: { type: Boolean, default: false },
    description: { type: String, default: '' },
    currentParcelId: { type: Number, default: null }
  },
  data() {
    return {
      loading: false,
      applying: false,
      done: false,
      selectedParcelIds: [],
      progress: { done: 0, total: 0 }
    }
  },
  computed: {
    projectMasterplans() {
      return this.$store.state.MasterplanStore.projectMasterplans || []
    },
    projectParcels() {
      return this.$store.state.ParcelStore.projectParcels || []
    },
    masterplanTree() {
      return MasterplanHelper.buildMasterplanTreeOptions(
        this.projectMasterplans
      )
    },
    // One group per masterplan (in depth-first project order, with each
    // node's real nesting depth for indentation), holding only the parcels
    // that are actually eligible to receive the copied description.
    // Masterplans with no eligible parcels are dropped, but the remaining
    // ones keep their true depth so indentation still reflects the real
    // hierarchy.
    eligibleGroups() {
      return this.masterplanTree
        .map(node => ({
          masterplanId: node.id,
          masterplanName: node.name,
          depth: node.depth,
          parcels: this.projectParcels.filter(
            p => p.masterplansId === node.id && p.id !== this.currentParcelId
          )
        }))
        .filter(group => group.parcels.length > 0)
    },
    eligibleParcels() {
      return this.eligibleGroups.reduce(
        (acc, group) => acc.concat(group.parcels),
        []
      )
    },
    allSelected() {
      return (
        this.eligibleParcels.length > 0 &&
        this.selectedParcelIds.length === this.eligibleParcels.length
      )
    },
    someSelected() {
      return this.selectedParcelIds.length > 0 && !this.allSelected
    }
  },
  watch: {
    visible(val) {
      if (val) this.onOpen()
    }
  },
  methods: {
    async onOpen() {
      this.reset()
      this.loading = true
      await this.$store.dispatch('ParcelStore/fetchProjectParcels')
      this.loading = false
    },
    reset() {
      this.done = false
      this.applying = false
      this.selectedParcelIds = []
      this.progress = { done: 0, total: 0 }
    },
    close() {
      this.$emit('close')
      this.$nextTick(() => this.reset())
    },
    toggleSelectAll(val) {
      this.selectedParcelIds = val ? this.eligibleParcels.map(p => p.id) : []
    },
    isGroupFullySelected(group) {
      return group.parcels.every(p => this.selectedParcelIds.includes(p.id))
    },
    isGroupIndeterminate(group) {
      const selectedCount = group.parcels.filter(p =>
        this.selectedParcelIds.includes(p.id)
      ).length
      return selectedCount > 0 && selectedCount < group.parcels.length
    },
    toggleGroup(group, val) {
      const ids = group.parcels.map(p => p.id)
      if (val) {
        const toAdd = ids.filter(id => !this.selectedParcelIds.includes(id))
        this.selectedParcelIds = [...this.selectedParcelIds, ...toAdd]
      } else {
        this.selectedParcelIds = this.selectedParcelIds.filter(
          id => !ids.includes(id)
        )
      }
    },
    async copyDescription() {
      this.applying = true
      this.progress = { done: 0, total: this.selectedParcelIds.length }
      const description = this.description
      for (const id of this.selectedParcelIds) {
        try {
          const res = await ParcelService.getParcelById(id)
          const parcel = res.data
          if (!parcel.properties) parcel.properties = {}
          parcel.properties.description = description
          await ParcelService.updateParcel(parcel)
        } catch (e) {
          // continue with remaining parcels on error
        }
        this.progress.done++
      }
      this.applying = false
      this.done = true
    }
  }
}
</script>

<style scoped>
.dialog-card {
  background: #1e1e1e;
}

.dialog-title-bar {
  display: flex;
  align-items: center;
  padding: 14px 20px;
}

.global-select-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: rgba(66, 165, 245, 0.06);
  border-radius: 6px;
  border: 1px solid rgba(66, 165, 245, 0.15);
}

.masterplan-group {
  border-left: 2px solid rgba(66, 165, 245, 0.35);
  border-radius: 0 6px 6px 0;
  overflow: hidden;
}

.group-header-row {
  display: flex;
  align-items: center;
  padding: 7px 12px;
  background: rgba(255, 255, 255, 0.04);
}

.group-header-label {
  display: flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  opacity: 0.6;
}

.group-icon {
  opacity: 0.7;
}

.group-count {
  margin-left: 5px;
  font-weight: 400;
  opacity: 0.6;
}

.group-parcels {
  padding: 4px 12px 8px;
}

.parcel-checkbox {
  margin-bottom: 2px !important;
}

.done-icon-wrap {
  display: inline-flex;
  padding: 12px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 50%;
}
</style>
